import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Mail, Phone } from 'lucide-react';
import { FAQMetaData } from '../layouts/MetaData/FAQMetaData';

const FAQ = () => {
    const [activeIndex, setActiveIndex] = useState(null);

    const faqs = [
        {
            question: "What products does Meatbuddy offer?",
            answer: "Meatbuddy provides a wide selection of high-quality, fresh meats, including chicken, mutton, and fish. We offer premium cuts, ready-to-cook options, seasoned meats, and specialty meats, ensuring there's something for every taste and cooking preference."
        },
        {
            question: "How do I place an order on Meat buddy?",
            answer: "Placing an order on Meatbuddy is quick and easy! Simply visit our website or mobile app, browse our selection, and add your desired products to your cart. Once you're ready, proceed to checkout and choose your preferred payment method—pay securely online or opt for Cash on Delivery (COD) if available in your area. Enjoy fresh, high-quality meat delivered straight to your doorstep!"
        },
        {
            question: "How is the quality of the meat maintained?",
            answer: "At Meatbuddy, we are committed to delivering unmatched freshness and quality. Our meats are sourced from trusted suppliers and handled with the utmost care. Using advanced packaging techniques and a strict cold-chain logistics system, we ensure that your order reaches you in perfect condition—fresh, flavourful, and ready to cook."
        },
        {
            question: "Is there a minimum order amount?",
            answer: "Yes, a minimum order requirement may apply, depending on your location and delivery area. To find out the specific minimum order amount for your region, please check our website or mobile app for the latest details."
        },
        {
            question: "Can I modify or cancel my order?",
            answer: "Yes, you can cancel your order within 1 hour of placing it. If you need to make any modifications, please contact our customer support team as soon as possible. While we'll do our best to accommodate changes, modifications may not be possible once the order has been processed or dispatched."
        },
        {
            question: "Do you offer same-day delivery?",
            answer: "Yes, we offer same-day delivery in selected areas, subject to product availability. For even faster service, express delivery options are available. Please check the estimated delivery time at checkout to confirm availability for your location."
        },
        {
            question: "What payment methods do you accept?",
            answer: "We accept a variety of payment methods, including: Credit/Debit cards (Visa, MasterCard, etc.), Net banking, UPI, and Cash on Delivery (COD)."
        },
        {
            question: "How do I track my order?",
            answer: "Once your order has been processed and dispatched, you will receive a tracking number via SMS or email. You can use this to track your delivery in real-time through the Meatbuddy website or mobile app, ensuring you stay updated every step of the way."
        },
        {
            question: "What is your refund policy?",
            answer: "For online payments, refunds will be processed within 48 hours and credited to your account within 5-7 working days. For Cash on Delivery (COD) orders, refunds will be issued as store credit, which can be used for future purchases on Meatbuddy."
        },
        {
            question: "Do you offer returns or replacements?",
            answer: "We offer a \"No Questions Asked\" return and replacement policy for any quality or freshness-related concerns. If you receive a damaged or subpar product, simply contact us within 24 hours of delivery, and we'll promptly arrange a replacement or refund. Your satisfaction is our priority!"
        },
        {
            question: "How do I contact customer service?",
            answer: "You can reach out to our customer service team via: Email: customersupport@meatbuddy.in, Phone: +91- 63647409404, or Live chat: Available on our website and Mobile app."
        },
        {
            question: "How can I unsubscribe from marketing emails?",
            answer: "If you no longer wish to receive marketing emails, you can unsubscribe by clicking the \"Unsubscribe\" link in any promotional email, or by contacting us at customersupport@meatbuddy.in"
        },
        {
            question: "Do you offer discounts or promotions?",
            answer: "Yes, we frequently offer discounts and promotions! Stay updated by checking our website or mobile app for the latest deals. You can also sign up for our newsletter to receive exclusive offers and special discounts directly in your inbox."
        },
        {
            question: "Where do you deliver?",
            answer: "We currently offer delivery in multiple locations across Bangalore. To check if we deliver to your area, please visit the \"Delivery Areas\" section on our website or mobile app."
        }
    ];

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <>
            <FAQMetaData />
            <div 
                className="pt-0"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header Section */}
                    <div className="text-center mb-10">
                        <h1 className="text-2xl md:text-3xl font-bold text-red-600 mb-3">
                            Frequently Asked Questions
                        </h1>
                        <div className="text-base text-gray-600 max-w-3xl mx-auto">
                            <p>
                                Welcome to the Meatbuddy FAQ page! Here, we've compiled answers to the
                                most common questions about our products, services, and policies to make
                                your shopping experience seamless. If you need further assistance, don't
                                hesitate to reach out—we're always happy to help!
                            </p>
                        </div>
                    </div>

                    {/* FAQ List */}
                    <div className="space-y-4 max-w-4xl mx-auto mb-10">
                        {faqs.map((faq, index) => (
                            <div 
                                key={index} 
                                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:border-red-100 transition-colors duration-200"
                            >
                                <button
                                    className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 focus:outline-none transition-colors duration-200"
                                    onClick={() => toggleFAQ(index)}
                                    aria-expanded={activeIndex === index}
                                >
                                    <span className={`text-sm font-semibold transition-colors duration-200 ${
                                        activeIndex === index ? 'text-red-600' : 'text-gray-700'
                                    }`}>
                                        {faq.question}
                                    </span>
                                    {activeIndex === index ? (
                                        <ChevronUp className="w-5 h-5 text-red-600 flex-shrink-0" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    )}
                                </button>
                                <div 
                                    className={`transition-all duration-300 ease-in-out ${
                                        activeIndex === index 
                                            ? 'max-h-96 opacity-100' 
                                            : 'max-h-0 opacity-0'
                                    } overflow-hidden`}
                                >
                                    <div className="px-6 py-4 text-gray-600 text-xs leading-relaxed bg-gray-50 border-t border-gray-100">
                                        {faq.answer}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <div className="bg-white rounded-lg p-6 shadow-md mb-10 max-w-4xl mx-auto">
                        <h2 className="text-lg font-semibold text-red-600 mb-6 text-center">
                            Still have questions?
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <a 
                                href="mailto:customersupport@meatbuddy.in" 
                                className="flex items-center justify-center gap-3 p-4 rounded-lg bg-red-50 hover:bg-red-100 transition-colors duration-200 group"
                            >
                                <Mail className="w-5 h-5 text-red-600" />
                                <span className="text-gray-700 text-sm group-hover:text-red-600 transition-colors duration-200">
                                    customersupport@meatbuddy.in
                                </span>
                            </a>
                            <a 
                                href="tel:+916364740904" 
                                className="flex items-center justify-center gap-3 p-4 rounded-lg bg-red-50 hover:bg-red-100 transition-colors duration-200 group"
                            >
                                <Phone className="w-5 h-5 text-red-600" />
                                <span className="text-gray-700 text-sm group-hover:text-red-600 transition-colors duration-200">
                                    +91- 6364749404
                                </span>
                            </a>
                        </div>
                    </div>

                    {/* Call to Action */}
                    <div className="text-center">
                        <p className="text-base text-gray-600 mb-5">
                            At Meatbuddy, we're committed to providing excellent customer service. If you couldn't find the answer to your question, please don't hesitate to contact us directly.
                        </p>
                        <a 
                            href="/contact" 
                            className="inline-block bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                        >
                            Contact Us
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FAQ;